import { useReverification, useUser } from '@clerk/nextjs';
import {
  CALENDAR_READONLY_SCOPE,
  CALENDAR_READONLY_SCOPES,
} from '@/code/core/services/availability/calendarScopes';
import { assertExternalWritesAllowed } from '@/code/core/services/previewSafety';
import { trpc } from '@/code/lib/utils/trpc';

/**
 * Shared Google Calendar (re)authorization flow used by both the
 * /verify-calendar-connection page and the CalendarScopeAutoHeal provider so
 * the reauthorize semantics never drift between the manual and automatic paths.
 *
 * Returns a function that, for the given `additionalScopes`:
 *  1. Persists the scope intent into Clerk publicMetadata FIRST when a calendar
 *     read scope is requested (reauthorize redirects away, so the write must
 *     land before navigation), enabling silent self-heal on future logins.
 *  2. Reauthorizes the existing Google account (or creates the external account
 *     if none is linked) and returns the Clerk verification redirect URL, or
 *     `undefined` if Google returned no URL.
 *
 * Wrapped in `useReverification` so Clerk surfaces its verification modal for
 * 2FA users instead of failing outright.
 */
export function useCalendarReauthorize() {
  const { user } = useUser();
  const persistIntent = trpc.user.persistCalendarScopeIntent.useMutation();

  return useReverification(async (additionalScopes: string[]) => {
    assertExternalWritesAllowed();
    // Persist intent before redirecting so the auto-heal provider can re-grant
    // silently later. Only when a calendar read scope is in the request — write
    // grants are out of scope for self-heal.
    if (additionalScopes.includes(CALENDAR_READONLY_SCOPE)) {
      try {
        await persistIntent.mutateAsync();
      } catch {
        // Non-fatal: a missing intent only disables future silent self-heal,
        // it must never block the user's reauthorize. Proceed regardless.
      }
    }

    // Clerk reports the provider un-prefixed (`google`); there's no `strategy`
    // on the ExternalAccount returned by this client.
    const googleAccount = user?.externalAccounts.find(
      (ex) => ex.provider === 'google',
    );

    if (googleAccount) {
      const reauth = await googleAccount.reauthorize({
        additionalScopes,
        redirectUrl: window.location.href,
      });
      return reauth?.verification?.externalVerificationRedirectURL?.href;
    }

    const res = await user?.createExternalAccount({
      strategy: 'oauth_google',
      redirectUrl: window.location.href,
      additionalScopes,
    });
    return res?.verification?.externalVerificationRedirectURL?.href;
  });
}

/** The read-only calendar scope set, re-exported for convenient call sites. */
export { CALENDAR_READONLY_SCOPES };
